import { Direction } from '@angular/cdk/bidi';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { PreloginEligibilityPopupComponent } from '@pages/product/containers/prelogin-eligibility-popup/prelogin-eligibility-popup.component';
import {
  EligibilityCheckDialogComponent,
  EligibilityCheckDialogData,
} from '@shared/components/eligibility-check-dialog/eligibility-check-dialog.component';
import {
  EligibilityResultDialogComponent,
  EligibilityResultDialogData,
} from '@shared/components/eligibility-result-dialog/eligibility-result-dialog.component';
import { CustomisableSection } from '@shared/models/customisable-section';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { Observable, Subject, combineLatest, distinctUntilChanged, filter, take } from 'rxjs';
import { PopupEligibilityComponent } from '../components/popup-eligibility/popup-eligibility.component';
import { EligibilitiesAndSelfRegEnum } from '../enums/eligibilities-and-selfregs.enum';
import { HomePageComponentNameEnum } from '../enums/homepage-components-name.enum';
import { ISiteSettings } from '../interfaces/site-settings.interfaces';
import { CustomisableSectionsService } from './customisable-sections.service';
import { SiteConfigService } from './site-config.service';

@Injectable({
  providedIn: 'root',
})
export class EligibilityRegistrationService {
  public customisableSections$ = this.customisableSectionsService.getCustomisableSections([
    'loggedouthomepage',
    'mtls_producthomepage_paragraph2',
  ]);
  public customVariables$: Observable<CustomisableVariables> = this.customVariableService.variables;
  public siteSettings$: Observable<ISiteSettings> = this.siteSettingsService.fetchSiteSettings();
  public onDestroy$: Subject<any> = new Subject<any>();

  constructor(
    private customVariableService: CustomisableVariablesService,
    private matDialog: MatDialog,
    private customisableSectionsService: CustomisableSectionsService,
    private siteConfig: SiteConfigService,
    private siteSettingsService: SiteSettingsService,
    private router: Router
  ) {}

  /**
   * Opens the eligibility checker modal
   */
  public openEligibilityCheck(eligibilityType: string): void {
    const direction = document.querySelector('html')?.dir as Direction;

    combineLatest([this.customVariables$, this.customisableSections$, this.siteSettings$])
      .pipe(take(1))
      .subscribe(
        ([customVariables, customisableSections, siteSettings]: [
          CustomisableVariables,
          CustomisableSection[],
          ISiteSettings,
        ]) => {
          const preloginOn =
            siteSettings?.switches?.pre_login_home_page &&
            this.siteConfig.getComponentData('header')?.default?.buttonContainerStyle === 'modern';

          if (eligibilityType === EligibilitiesAndSelfRegEnum.NON_TM_LINK) {
            this.matDialog.open(PopupEligibilityComponent, {
              panelClass: 'popup-eligibility',
              data: {
                title: customVariables.eligibility_popup_title,
                body: customVariables.eligibility_popup_body,
                homePageComponentName: HomePageComponentNameEnum.ProductHomepageMastercardComponent,
              },
              disableClose: true,
              direction,
            });
          } else {
            const eligibilityCheck$ = this.matDialog.open<
              EligibilityCheckDialogComponent,
              EligibilityCheckDialogData,
              'invalid' | 'success'
            >(EligibilityCheckDialogComponent, {
              disableClose: true,
              scrollStrategy: new NoopScrollStrategy(),
              direction,
              panelClass: 'eligibility-check-dialog',
              data: {
                title: 'check_eligibility_title',
                eligibilityCheckTracking: 'MC Registration - Check eligibility button',
                translations: [
                  customVariables.register_paragraph_1 || 'ProductHomepage:Paragraph1',
                  customisableSections[1]?.content?.body || 'ProductHomepage:Paragraph2',
                  customVariables.mastercard_not_charged_message ||
                    'UserRegistrationMastercardComponent:CardNotCharged',
                ],
              },
            });
            eligibilityCheck$.afterClosed().subscribe((res) => {
              // if prelogin feature is off success, error messages are in popup, else they go to their pages
              if (preloginOn) {
                if (res === 'invalid') {
                  this.router.navigate(['/product/user-ineligible']);
                } else if (res === 'success') {
                  this.router.navigate(['/product/user-eligible']);
                }
                return;
              }
              if (res === 'invalid') {
                this.matDialog.open<EligibilityResultDialogComponent, EligibilityResultDialogData>(
                  EligibilityResultDialogComponent,
                  {
                    disableClose: true,
                    direction,
                    data: {
                      type: 'invalid',
                      subtitle: 'eligibility_unsuccessful_subtitle',
                      tracking: 'MC Registration - Eligibility fail pop up',
                      translations: ['UserIneligibleMastercard:Paragraph1', 'UserIneligibleMastercard:Paragraph2'],
                      customVarParagraph: ['user_ineligible_mastercard_1'],
                    },
                  }
                );
              } else if (res === 'success') {
                this.matDialog.open(EligibilityResultDialogComponent, {
                  disableClose: true,
                  direction,
                  data: {
                    type: 'success',
                    subtitle: 'eligibility_success_subtitle',
                    tracking: 'MC Registration - Eligibility success pop up',
                    continueTracking: 'MC Registration - Eligibility success continue',
                    translations: ['UserEligibleMastercard:Paragraph1'],
                    customVarParagraph: ['user_eligible_mastercard_1'],
                  },
                });
              }
            });
          }
        }
      );
  }

  public getEligibilityCheckType(eligibilityType: string): void {
    const headerConfigStyle = this.siteConfig.getComponentData('header')?.default;
    if (headerConfigStyle?.buttonContainerStyle === 'modern') {
      this.openEligibilityCheck(eligibilityType);
      return;
    }
    if (eligibilityType === EligibilitiesAndSelfRegEnum.NON_TM_LINK) {
      // NON_TM_LINK eligibility type other than Mastercard in prelogin feature
      this.checkGenericEligibility();
    } else {
      this.openPreloginEligibilityCheck(eligibilityType, headerConfigStyle?.showCustomExternalEligibility);
    }
  }

  public checkGenericEligibility() {
    // NON_TM_LINK eligibility type other than Mastercard in prelogin feature
    this.customVariables$
      .pipe(
        filter((value: CustomisableVariables) => !!value?.eligibility_popup_title && !!value?.eligibility_popup_body),
        distinctUntilChanged(),
        take(1)
      )
      .subscribe((customisableVariables: CustomisableVariables) => {
        const direction = (document.querySelector('html')?.dir as Direction) || 'ltr';

        this.matDialog.open(PopupEligibilityComponent, {
          panelClass: ['prelogin-eligibility-popup', 'popup-eligibility'],
          data: {
            title: customisableVariables.eligibility_popup_title,
            body: customisableVariables.eligibility_popup_body,
          },
          disableClose: true,
          direction,
        });
      });
  }

  public openPreloginEligibilityCheck(eligibilityType: string, customExternalEligibility?: boolean): void {
    const direction = document.querySelector('html')?.dir as Direction;

    this.matDialog.open(PreloginEligibilityPopupComponent, {
      panelClass: 'prelogin-eligibility-popup',
      maxWidth: '500px',
      data: {
        type: eligibilityType,
        customExternalEligibility,
      },
      disableClose: true,
      direction,
    });
  }
}
